import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, apiClient } from '../App';
import { CheckCircle2, Truck, Loader2, ArrowRight, FileText, Send, Users, Briefcase, Shield, MessageCircle, Star, CreditCard, Calendar, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Walkthrough content by role
const walkthroughContent = {
  customer: {
    title: "Welcome to True Choice Enterprise!",
    subtitle: "Here's how to get your items moved:",
    steps: [
      { icon: FileText, title: "1. Post a Moving Job", description: "Click 'Post New Job' to describe what you need moved. Include pickup/delivery locations, dates, and any special requirements." },
      { icon: Send, title: "2. Receive Quotes", description: "Verified carriers will review your job and send competitive quotes directly to you. No broker fees!" },
      { icon: MessageCircle, title: "3. Chat with Carriers", description: "Use the chat feature to ask questions, negotiate, or discuss details before accepting a quote." },
      { icon: CheckCircle2, title: "4. Accept & Move", description: "Accept the best quote and coordinate with your carrier. Leave a review after completion!" }
    ],
    cta: "Post Your First Job"
  },
  carrier: {
    title: "Welcome to True Choice Enterprise!",
    subtitle: "Here's how to grow your business:",
    steps: [
      { icon: FileText, title: "1. Browse Available Jobs", description: "View jobs posted by customers in your area. Filter by job type and location." },
      { icon: Send, title: "2. Send Quotes", description: "Submit competitive quotes directly to customers. Include your price and estimated delivery." },
      { icon: Users, title: "3. Hire Drivers", description: "Post driver job openings and review applicant profiles." },
      { icon: MessageCircle, title: "4. Chat with Customers", description: "Use real-time chat to discuss job details and build relationships." }
    ],
    cta: "Browse Available Jobs"
  },
  driver: {
    title: "Welcome to True Choice Enterprise!",
    subtitle: "Here's how to find driving opportunities:",
    steps: [
      { icon: Users, title: "1. Complete Your Profile", description: "Add your license type, experience, certifications, and a brief bio." },
      { icon: Briefcase, title: "2. Browse Driver Jobs", description: "Search for driving positions from verified carriers." },
      { icon: Shield, title: "3. Get Discovered", description: "Carriers can view your profile and contact you directly for opportunities." },
      { icon: Send, title: "4. Apply & Connect", description: "Apply to positions with a cover letter. Carriers will contact you directly." }
    ],
    cta: "Complete Your Profile"
  }
};

export default function SubscriptionSuccess({ type = 'subscription' }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [status, setStatus] = useState('checking');
  const [paymentPreview, setPaymentPreview] = useState(null);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else if (type === 'subscription' && user?.subscription_status === 'active') {
      setStatus('success');
      loadPaymentPreview();
      setShowWalkthrough(true);
    }
  }, [searchParams, user]);

  const checkPaymentStatus = async (sessionId) => {
    try {
      const endpoint = type === 'subscription' 
        ? `/subscriptions/status/${sessionId}`
        : `/payments/background-check/status/${sessionId}`;
      
      const res = await apiClient.get(endpoint);
      
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        
        if (type === 'subscription') {
          const updatedUser = { ...user, subscription_status: 'active' };
          updateUser(updatedUser);
          toast.success('Subscription activated!');
          loadPaymentPreview();
          setShowWalkthrough(true);
        } else {
          const checkId = searchParams.get('check_id');
          if (checkId) {
            await apiClient.post(`/background-checks/${checkId}/process`);
            toast.success('Background check processing started!');
          }
        }
      } else {
        setTimeout(() => checkPaymentStatus(sessionId), 2000);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      setStatus('error');
    }
  };

  const loadPaymentPreview = async () => {
    try {
      const res = await apiClient.get('/subscriptions/payment-preview');
      setPaymentPreview(res.data);
    } catch (error) {
      console.error('Failed to load payment preview');
    }
  };

  const handleCtaClick = () => {
    const role = user?.role || 'customer';
    navigate(`/${role}/dashboard`);
  };

  const content = walkthroughContent[user?.role] || walkthroughContent.customer;

  // Background check success
  if (type !== 'subscription' && status === 'success') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <Truck className="h-8 w-8 text-orange-500" />
            <span className="text-xl font-bold text-slate-900">True Choice Enterprise</span>
          </Link>
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="h-10 w-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Background Check Started!</h2>
          <p className="text-slate-600 mb-6">Results will be available shortly on the driver's profile.</p>
          <Button onClick={() => navigate('/carrier/dashboard')} className="btn-accent w-full">
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl shadow-lg p-8 w-full text-center ${showWalkthrough ? 'max-w-2xl' : 'max-w-md'}`}>
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <Truck className="h-8 w-8 text-orange-500" />
          <span className="text-xl font-bold text-slate-900">True Choice Enterprise</span>
        </Link>

        {status === 'checking' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
              <Loader2 className="h-12 w-12 text-orange-500 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Processing Payment</h2>
            <p className="text-slate-600">Please wait while we verify your payment...</p>
          </>
        )}

        {status === 'success' && showWalkthrough && (
          <div className="text-left">
            {/* Success Header with Payment Preview */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-10 w-10 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">{content.title}</h2>
              <p className="text-slate-600 mt-2">{content.subtitle}</p>
            </div>

            {/* Payment Preview Card */}
            {paymentPreview && (
              <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white rounded-xl p-5 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                      <CreditCard className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">Subscription Active</p>
                      <p className="font-bold text-lg">Monthly Plan</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-black">${paymentPreview.amount}<span className="text-sm text-slate-400">/mo</span></p>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm border-t border-white/10 pt-3">
                  <div className="flex items-center gap-2 text-slate-300">
                    <Calendar className="h-4 w-4" />
                    <span>Billed monthly</span>
                  </div>
                  <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-semibold">ACTIVE</span>
                </div>
              </div>
            )}

            {/* Walkthrough Steps */}
            <div className="space-y-3 mb-6">
              {content.steps.map((step, index) => (
                <div 
                  key={index}
                  className={`flex gap-4 p-4 rounded-xl transition-all cursor-pointer ${
                    currentStep === index ? 'bg-orange-50 border-2 border-orange-500' : 'bg-slate-50 border border-slate-200'
                  }`}
                  onClick={() => setCurrentStep(index)}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    currentStep === index ? 'bg-orange-500 text-white' : 'bg-white text-slate-600'
                  }`}>
                    <step.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-sm">{step.title}</h3>
                    <p className="text-xs text-slate-600 mt-1">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={handleCtaClick} className="btn-accent flex-1" data-testid="walkthrough-cta">
                {content.cta} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={() => setShowWalkthrough(false)} className="flex-1">
                Skip Tutorial
              </Button>
            </div>
          </div>
        )}

        {status === 'success' && !showWalkthrough && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-10 w-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">You're All Set!</h2>
            
            {/* Payment Preview */}
            {paymentPreview && (
              <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Subscription</span>
                  <span className="font-bold text-green-600">Active</span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-slate-600">Amount</span>
                  <span className="font-bold">${paymentPreview.amount}/month</span>
                </div>
              </div>
            )}
            
            <div className="space-y-3">
              <Button onClick={handleCtaClick} className="btn-accent w-full">
                Go to Dashboard <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <button onClick={() => setShowWalkthrough(true)} className="text-orange-500 hover:text-orange-600 text-sm">
                Show me how to use the app
              </button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Something went wrong</h2>
            <p className="text-slate-600 mb-6">Please contact support if this issue persists.</p>
            <Button onClick={() => navigate('/dashboard')} className="btn-primary w-full">
              Go to Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
